import React, { Component } from 'react';
import PropTypes from 'prop-types';
import PropTypesImmutable from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import isEqual from 'lodash/isEqual';
import { Row, Col } from 'antd';
import { FORM_ADD_CONSUMPTION_POINT } from '../../constants/formKeys';
import {
  meteringPointType,
  meteringPointDataSource,
} from '../../constants/classificators';
import { gas, meteringPointFields } from '../../constants/meteringPoint';
import { DateField, ClassificatorField, SelectField } from '../../atoms';
import { getEntityById } from '../../utils/asyncHelpers';
import {
  measurement as meteringSchema,
  avpMeteringPointScheme,
} from '../../schemas';
import { goToMeteringPointList } from '../../utils/gotoLink';
import { consumerFields } from '../../constants/consumer';
import { getSelectedPointId } from '../../reducers/consumptionPointAddReducer';
import { updateConsumptionPoint } from '../../actions/actionCreators/meteringPointActionCreators';
import {
  formValueSelector,
  clearFormState,
} from '../../reducers/reactFinalFormReducer';
import FormStateToRedux from '../FormStateToRedux';
import SubmitErrorToast from '../SubmitErrorToast';
import { makeRffValidateFromLegacy } from '../rffLegacyValidateAdapter';
import validate from './validationSchema';
import classifiersStore from '../../utils/classifiersStore';

export class ConsumptionPointAdd extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    selectedMeteringPoint: PropTypesImmutable.map,
    ownerId: PropTypes.number.isRequired,
    consumer: PropTypesImmutable.map,
    initialValues: PropTypes.object,
    formApiRef: PropTypes.func,
    updateConsumptionPoint: PropTypes.func.isRequired,
    clearFormState: PropTypes.func.isRequired,
  };

  setFormApi = (form) => {
    if (this._form === form) return;
    this._form = form;
    if (this.props.formApiRef) {
      this.props.formApiRef(form);
    }
  };

  onSubmit = async (values) => {
    const {
      t,
      selectedMeteringPoint,
      ownerId,
      consumer,
      updateConsumptionPoint,
      clearFormState,
    } = this.props;

    if (!selectedMeteringPoint) {
      return { [FORM_ERROR]: t('info.metringPointError') };
    }
    if (!ownerId) {
      return { [FORM_ERROR]: t('info.ownerShouldBeFilled') };
    }
    if (!consumer) {
      return { [FORM_ERROR]: t('info.consumerShouldBeFilled') };
    }

    const consumerJs = consumer.toJS();
    if (typeof consumerJs[consumerFields.ID] === 'string') {
      delete consumerJs[consumerFields.ID];
    }

    const payload = {
      ...values,
      [meteringPointFields.LEGAL_ENTITY_ID]: ownerId,
      [meteringPointFields.CONSUMER]: consumerJs,
      [meteringPointFields.METERING_POINT_TYPE]: meteringPointType.CONSUMPTION,
    };
    // Backend generates the id on creation; strip any id carried over from AVP/form state.
    delete payload[meteringPointFields.ID];

    const result = await updateConsumptionPoint(payload);
    if (result?.error) return;
    clearFormState(FORM_ADD_CONSUMPTION_POINT);
    goToMeteringPointList();
  };

  render() {
    const { t, selectedMeteringPoint, initialValues } = this.props;

    const isAvp =
      selectedMeteringPoint &&
      selectedMeteringPoint.get(meteringPointFields.DATA_SOURCE) ===
        meteringPointDataSource.AVP;

    const options = classifiersStore.getBooleanProperty('IS_HYDROGEN_ENABLED')
      ? [
          {
            value: gas.HYDROGEN,
            label: t('info.gas.hydrogen'),
          },
          {
            value: gas.BIOMETHANE,
            label: t('info.gas.biomethane'),
          },
          {
            value: gas.LIQUID_BIOMETHANE,
            label: t('info.gas.liquidBiomethane'),
          },
        ]
      : [
          {
            value: gas.BIOMETHANE,
            label: t('info.gas.biomethane'),
          },
        ];

    const rffValidate = makeRffValidateFromLegacy(
      validate,
      this.props,
      initialValues
    );

    return (
      <Form
        onSubmit={this.onSubmit}
        initialValues={initialValues}
        initialValuesEqual={isEqual}
        keepDirtyOnReinitialize
        validate={rffValidate}
        render={({ handleSubmit, form }) => {
          this.setFormApi(form);
          return (
            <form onSubmit={handleSubmit}>
              <FormStateToRedux form={FORM_ADD_CONSUMPTION_POINT} />
              <SubmitErrorToast />
              <Row gutter={32}>
                <Col span={8}>
                  <Field
                    name={meteringPointFields.START_DATE}
                    component={DateField}
                    label={t('info.startDate')}
                    required
                    disabled={isAvp}
                  />
                </Col>
                <Col span={8}>
                  <Field
                    name={meteringPointFields.END_DATE}
                    component={DateField}
                    label={t('info.endDate')}
                    disabled={isAvp}
                  />
                </Col>
                <Col span={8}>
                  <Field
                    name={meteringPointFields.GAS}
                    component={SelectField}
                    required
                    label={t('info.gas.title')}
                    options={options}
                  />
                </Col>
              </Row>
              <Row gutter={32}>
                <Col span={8}>
                  <Field
                    name={meteringPointFields.CONSUMPTION_TYPE}
                    component={ClassificatorField}
                    classificatorType={
                      ClassificatorField.types.consumptionUsage
                    }
                    label={t('info.consumptionType')}
                    required
                  />
                </Col>
              </Row>
            </form>
          );
        }}
      />
    );
  }
}

const rffSelector = formValueSelector(FORM_ADD_CONSUMPTION_POINT);

const mapStateToProps = (state) => {
  const selectedPointId = getSelectedPointId(state);

  let point = getEntityById(state, avpMeteringPointScheme, selectedPointId);
  if (!point) {
    point = getEntityById(state, meteringSchema, selectedPointId);
  }

  const formValues = {
    [meteringPointFields.GAS]: rffSelector(state, meteringPointFields.GAS),
    [meteringPointFields.START_DATE]: rffSelector(
      state,
      meteringPointFields.START_DATE
    ),
    [meteringPointFields.END_DATE]: rffSelector(
      state,
      meteringPointFields.END_DATE
    ),
    [meteringPointFields.CONSUMPTION_TYPE]: rffSelector(
      state,
      meteringPointFields.CONSUMPTION_TYPE
    ),
  };

  let pointValues = point;
  if (point) {
    pointValues = point.toJS();
    if (
      point.get(meteringPointFields.DATA_SOURCE) === meteringPointDataSource.AVP
    ) {
      pointValues[meteringPointFields.GAS] =
        formValues[meteringPointFields.GAS];
      pointValues[meteringPointFields.CONSUMPTION_TYPE] =
        formValues[meteringPointFields.CONSUMPTION_TYPE];
    }
  }

  return {
    selectedMeteringPoint: point,
    initialValues: {
      ...formValues,
      ...pointValues,
    },
  };
};

const mapDispatchToProps = {
  updateConsumptionPoint,
  clearFormState,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ConsumptionPointAdd);
